import * as React from "react";
import PropTypes from "prop-types";
import uniqueId from "lodash-es/uniqueId";
import { connect } from "react-redux";

import "./styles.css";
import { useElementDOMProperty } from "../../hooks";
import { fieldChanged, focusOn } from "../../store/actions";
import useCloseOnBlur from "../use-close-on-blur";

const mapDispatchToProps = dispatch => ({
  fieldChanged: fieldName => dispatch(fieldChanged(fieldName)),
  focusOn: fieldName => dispatch(focusOn(fieldName))
});

const BaseInput = ({
  fieldName,
  fieldChanged,
  focusOn,
  focusedField,
  value,
  setValue,
  placeholder,
  error,
  type,
  min
}) => {
  const [id] = React.useState(() => uniqueId("base-input-label-"));

  const onFocus = useCloseOnBlur(focusOn, fieldName, focusedField);

  const onInputChangeAndNotify = React.useCallback(
    ev => {
      setValue(ev.target.value);
      if (fieldName) {
        fieldChanged(fieldName);
      }
    },
    [setValue, fieldName, fieldChanged]
  );

  const [focused, inputRef] = useFocused();

  const {
    domProperty: errorTextHeight,
    elementRef: errorTextRef
  } = useElementDOMProperty(
    { getDOMProperty: el => el.offsetHeight, defaultValue: 0 },
    [error, focused]
  );

  return (
    <div styleName="input-container" id={`input-field-name-${fieldName}`}>
      <input
        className={[
          value === "" || value === undefined ? "empty" : "non-empty",
          error === "" || focused ? "valid" : "invalid"
        ].join(" ")}
        styleName="input"
        id={id}
        value={value || ""}
        onChange={onInputChangeAndNotify}
        ref={inputRef}
        onClick={onFocus}
        type={type || "text"}
        min={min}
      ></input>
      <label styleName="placeholder" htmlFor={id}>
        {placeholder}
      </label>
      <div styleName="focus-border"></div>
      <div styleName="error" style={{ height: errorTextHeight }}>
        <div ref={errorTextRef}>{focused ? "" : error}</div>
      </div>
    </div>
  );
};

BaseInput.propTypes = {
  fieldName: PropTypes.string,
  fieldChanged: PropTypes.func.isRequired,
  focusOn: PropTypes.func.isRequired,
  focusedField: PropTypes.string,
  placeholder: PropTypes.string.isRequired,
  validator: PropTypes.func,
  value: PropTypes.string,
  setValue: PropTypes.func.isRequired,
  error: PropTypes.string,
  type: PropTypes.string,
  min: PropTypes.number
};

const useFocused = () => {
  const inputRef = React.useRef();
  const [focused, setFocused] = React.useState(false);
  const focus = React.useCallback(() => setFocused(true), [setFocused]);
  const blur = React.useCallback(() => setFocused(false), [setFocused]);
  React.useEffect(() => {
    if (inputRef.current) {
      const curr = inputRef.current;
      setFocused(curr === document.activeElement);
      const doEvents = name => {
        curr[name]("blur", blur);
        curr[name]("focus", focus);
      };
      doEvents("addEventListener");
      return () => doEvents("removeEventListener");
    }
  }, [inputRef, blur, focus]);

  return [focused, inputRef];
};

export default connect(
  ({ focusedField }, ownProps) => ({ ...ownProps, focusedField }),
  mapDispatchToProps
)(BaseInput);
